// helpdesk-frontend/src/components/Layout.js

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import AnnouncementTopBar from "./Announcements/AnnouncementTopBar";
import styles from "./Layout.module.css";
import { useUI } from "../hooks/useUI";
import { useAuth } from "../hooks/useAuth";
import { useNotifications } from "../hooks/useNotifications";
import { useToast } from "../context/ToastContext";
import { apiService } from "../api/apiService";
import { translatePath } from "../utils/routeHelpers";
import { pobierzZPamieci, uniewaznij, KLUCZE_POWLOKI } from "../hooks/usePamiecPowloki";

import AppFooter from "./AppFooter/AppFooter";
import MyNewTicketModal from "./Tickets/NewTicketModal/MyNewTicketModal";
import AnnouncementModal from "./Announcements/AnnouncementModal";
import QuickAccessModal from "./QuickAccessModal/QuickAccessModal";
import WyszukiwarkaGlobalna from "./Wyszukiwarka/WyszukiwarkaGlobalna";
import TermsOfServiceModal from "./Terms/TermsOfServiceModal";
import ProfileSetupModal from "./Registration/ProfileSetupModal";
import { useKeyboardNavigation } from "../hooks/useKeyboardNavigation";
import ActivityTracker from "./ActivityTracker";
import MaskaNagrania from "./MaskaNagrania/MaskaNagrania";
import OchronaTresci from "./OchronaTresci/OchronaTresci";
/* [BC-10 2026-09-18] Pasek trybu „zaloguj jako". Właściciel: „chcę mieć tylko pasek na samej górze,
   który pokaże mi opcję powrotu do swojego konta". Renderuje się WYŁĄCZNIE w sesji podglądu
   (sam zwraca null poza nią), więc dla zwykłego pracownika nie zmienia nic. */
import PasekPodgladu from "./PasekPodgladu/PasekPodgladu";
// [polecenie właściciela 2026-09-16] Znak wodny + blokada wydruku na danych wrażliwych.
// Jeden punkt wpięcia dla WSZYSTKICH chronionych obszarów — patrz `obszaryChronione.js`.
import OchronaEkranu from "./OchronaEkranu/OchronaEkranu";
import ErrorBoundary from "./ErrorBoundary";
// [HASLA-LOKALNE 2026-09-27] Baner „hasło wygasa za N dni” (konto lokalne, ostatni tydzień ważności).
import BanerWygasaniaHasla from "./ZmianaHasla/BanerWygasaniaHasla";
// [polecenie właściciela 2026-10-02] Baner „Tryb testowy powiadomień” — nad nagłówkiem, dla osób bez dostępu do integracji.
import BanerTrybuTestowego from "./TrybTestowy/BanerTrybuTestowego";
// [polecenie właściciela 2026-10-03] Okno „Wygląd aplikacji” — raz na konto, PO oknach obowiązkowych.
// Import statyczny (bez lazy): leniwy plik dokładałby pobranie przy wejściu na /sygnalista (DWS-01-b).
import { OknoWygladuStartowe } from "./OknoWygladu/OknoWygladu";

const TeamsChatWidget = React.lazy(() => import("./TeamsChat/TeamsChatWidget"));

const Layout = () => {
    const { isSidebarCollapsed, isNewTicketModalOpen, closeNewTicketModal } = useUI();
    const { user, podgladAktywny, isModuleGloballyHidden, isModuleRestricted } = useAuth();

    /* ── [polecenie właściciela 2026-09-21] „ukryłem komunikaty, a dalej się pojawiają" ──────────
       Ukrycie modułu „Komunikaty" zdjęło pozycję menu i (od tej fali) kartę pulpitu, ale KOMUNIKATY
       PRZYCHODZĄ SAME: czarny pasek nad nagłówkiem i bramka pełnoekranowa, której nie da się pominąć,
       dopóki się jej nie odczyta. Oba wejścia siedzą TUTAJ, w powłoce, i żadne z nich o widoczność
       modułu nie pytało — pracownik firmy, która komunikaty wyłączyła (na produkcji `announcements`
       ma IsVisible=0), dalej dostawał okno blokujące cały ekran.

       To WIDOCZNOŚĆ, nie autoryzacja (A15-06): API `/announcements/pending` i `/announcements/topbar`
       odpowiadają dalej, a nadane komunikaty czekają nieodczytane — po ponownym włączeniu modułu
       bramka wraca. Znika wyłącznie WEJŚCIE, czyli to, co wchodzi człowiekowi na ekran.

       ⚠️ Obie drogi ukrycia, bo to dwa różne predykaty: globalnie dla firmy (ModuleVisibility)
       i deny-lista konta (User.RestrictedModules). Osłona `typeof` jest potrzebna — Layout renderują
       zapadki z atrapą `useAuth`, która oddaje samo konto, a powłoka nie ma prawa wysypać się
       przez brakującą funkcję. Brak funkcji = nic nie jest ukryte (fail-open, jak w `Sidebar`). */
    const modulUkryty = (kod) =>
        (typeof isModuleGloballyHidden === "function" && isModuleGloballyHidden(kod))
        || (typeof isModuleRestricted === "function" && isModuleRestricted(kod));
    const komunikatyWidoczne = !modulUkryty("announcements");

    /* [zgłoszenie właściciela 2026-09-19] Okna WYMAGANE (uzupełnienie danych, regulamin, komunikat)
       blokują cały ekran — i słusznie, bo pracownik ma je przeczytać. Ale administrator OGLĄDAJĄCY
       cudze konto utykał na nich i nie mógł sprawdzić niczego. Cytat: „potrzebuję móc te komunikaty
       móc zamknąć — ale to nie zwalnia z obowiązku odczytania i uzupełnienia go przez pracownika".
       Zamknięcie jest WYŁĄCZNIE lokalne i WYŁĄCZNIE na czas oglądania: nic nie idzie na serwer,
       nic nie jest odhaczane, a stan kasuje się przy wyjściu z trybu (efekt niżej). */
    const [zamknietePodczasPodgladu, setZamknietePodczasPodgladu] = useState(() => new Set());
    const zamknijWPodgladzie = useCallback((klucz) => {
        setZamknietePodczasPodgladu((poprzednie) => new Set(poprzednie).add(klucz));
    }, []);
    useEffect(() => {
        if (!podgladAktywny) setZamknietePodczasPodgladu(new Set());
    }, [podgladAktywny]);
    const {
        registerAnnouncementCallback, registerTermsCallback, registerToastCallback,
        registerAnnouncementReadCallback, registerTermsAcceptedCallback,
        markAsRead,
    } = useNotifications();
    const { showToast } = useToast();
    const navigate = useNavigate();
    /* [A16-15] Adres bieżącej strony — podawany do ErrorBoundary treści jako `resetKey`, żeby
       przejście na inną podstronę samo skasowało ekran błędu. Czytamy go z window.location, a nie
       z useLocation(): Layout jest elementem trasy nadrzędnej, więc każda zmiana adresu i tak
       przerenderowuje go PO aktualizacji historii przez router — wartość jest więc zawsze świeża,
       a Layout nie zaczyna wymagać kolejnego hooka routera od swoich konsumentów. */
    const sciezkaStrony = typeof window !== "undefined" ? window.location.pathname : "";

    // Detekcja nawigacji klawiaturą (Tab vs Mouse) — globalny
    useKeyboardNavigation();

    const [pendingAnnouncements, setPendingAnnouncements] = useState([]);
    const [pendingTerms, setPendingTerms] = useState(null);
    const [registrationStatus, setRegistrationStatus] = useState(null);

    /* [WYDAJNOŚĆ 2026-09-12] TOŻSAMOŚĆ KONTA, A NIE OBIEKT KONTA.
       BYŁO ŹLE: efekt startowy powłoki miał w zależnościach cały obiekt `user`. `AuthContext`
       woła `setUser` DWA RAZY na każde wejście do aplikacji — raz optymistycznie z localStorage,
       drugi raz po `GET /api/users/me` (warunek `!hadSignalRToken` po F5 jest zawsze prawdziwy,
       bo token SignalR żyje wyłącznie w pamięci). Treść jest ta sama, ale OBIEKT jest nowy, więc
       React uznawał zależność za zmienioną i cała powłoka leciała drugi raz.
       ZMIERZONE NA PRODUKCJI (3 dni): /api/registration/status 264, /api/announcements/pending 301,
       /api/terms/pending 326 — przy ~140 wejściach do aplikacji, czyli ok. DWA razy na wejście.
       TERAZ zależnością jest identyfikator konta: zmiana danych profilu nie odpala sprawdzeń
       ponownie, a przelogowanie (inne id) — owszem. Zapadka: `powlokaBezKaskad.test.js`. */
    const idKonta = user?.id ?? user?.email ?? null;

    /* Bez `AbortController` (apiService nie przyjmuje `signal`, a przerwanie zepsułoby
       współdzielenie pobrania): odcinamy SKUTEK — po odmontowaniu nie zapisujemy już stanu. */
    const zywy = useRef(true);
    useEffect(() => {
        zywy.current = true;
        return () => { zywy.current = false; };
    }, []);

    /* [2026-10-03] Czy powłoka już SPRAWDZIŁA trzy okna obowiązkowe. Stany startowe (null / []) wyglądają
       jak „nic nie czeka”, więc bez tej flagi okno „Wygląd aplikacji” mignęłoby przed regulaminem. */
    const [bramkiSprawdzone, setBramkiSprawdzone] = useState({ regulamin: false, rejestracja: false, komunikaty: false });
    const oznaczSprawdzona = useCallback((bramka) => {
        if (!zywy.current) return;
        setBramkiSprawdzone((p) => (p[bramka] ? p : { ...p, [bramka]: true }));
    }, []);

    // === TERMS CHECK ===
    // `wymus` = pomiń pamięć sesji (SignalR, powrót 403 „regulamin nieprzyjęty", akceptacja).
    const checkTerms = useCallback(async (wymus = false) => {
        try {
            const dane = await pobierzZPamieci(KLUCZE_POWLOKI.REGULAMIN, {
                pobieracz: () => apiService.getPendingTerms().then((r) => r?.data ?? null),
                tozsamosc: idKonta,
                wymus,
            });
            if (zywy.current) setPendingTerms(dane || null);
        } catch (error) {
            console.error("Błąd pobierania regulaminu:", error);
        } finally {
            oznaczSprawdzona("regulamin");
        }
    }, [idKonta, oznaczSprawdzona]);

    // === REGISTRATION / ONBOARDING CHECK (gate 2. kroku — formularz konfiguracji konta) ===
    const checkRegistration = useCallback(async (wymus = false) => {
        try {
            const dane = await pobierzZPamieci(KLUCZE_POWLOKI.REJESTRACJA, {
                pobieracz: () => apiService.getRegistrationStatus().then((r) => r?.data ?? null),
                tozsamosc: idKonta,
                wymus,
            });
            if (zywy.current) setRegistrationStatus(dane || null);
        } catch (error) {
            if (zywy.current) setRegistrationStatus(null);
        } finally {
            oznaczSprawdzona("rejestracja");
        }
    }, [idKonta, oznaczSprawdzona]);

    // === ANNOUNCEMENTS CHECK ===
    const checkAnnouncements = useCallback(async (wymus = false) => {
        try {
            const dane = await pobierzZPamieci(KLUCZE_POWLOKI.KOMUNIKATY, {
                pobieracz: () => apiService.getPendingAnnouncements().then((r) => r?.data ?? []),
                tozsamosc: idKonta,
                wymus,
            });
            if (zywy.current) setPendingAnnouncements(dane || []);
        } catch (error) {
            console.error("Błąd pobierania komunikatów:", error);
        } finally {
            oznaczSprawdzona("komunikaty");
        }
    }, [idKonta, oznaczSprawdzona]);

    /* Po każdej z tych bramek stan na serwerze WŁAŚNIE się zmienił — pamięć sesji musi ustąpić,
       inaczej okno regulaminu/onboardingu wróciłoby z zapamiętanej odpowiedzi. */
    const handleTermsAccepted = useCallback(() => {
        setPendingTerms(null);
        /* [PRZEGLAD ADWERSARIALNY 2026-09-12] Policzona lista „Czeka na Twoja decyzje” siedzi pod OSOBNYM
           kluczem i zawiera juz gotowy wiersz „Regulamin do akceptacji”. Uniewaznienie samego regulaminu
           go stamtad NIE zdejmuje — czlowiek akceptowal regulamin, wracal na pulpit w ciagu 30 s i wiersz
           dalej tam stal. Kasujemy oba klucze. */
        uniewaznij(KLUCZE_POWLOKI.AKCEPTACJE);
        checkRegistration(true);
        checkAnnouncements(true);
    }, [checkRegistration, checkAnnouncements]);

    const handleRegistrationCompleted = useCallback(() => {
        checkRegistration(true);
        checkAnnouncements(true);
    }, [checkRegistration, checkAnnouncements]);

    // Komunikat przeczytany — kolejka na serwerze jest krótsza, więc pytamy na nowo.
    const handleAnnouncementRead = useCallback(() => { checkAnnouncements(true); }, [checkAnnouncements]);

    // Check on mount — jedno pobranie na sesję karty (patrz `usePamiecPowloki`).
    useEffect(() => {
        if (!idKonta) return;
        // Inne konto = sprawdzenia od nowa (flagi poprzedniego konta nie mogą otworzyć okna wyglądu przed bramką).
        setBramkiSprawdzone((p) => (p.regulamin || p.rejestracja || p.komunikaty
            ? { regulamin: false, rejestracja: false, komunikaty: false } : p));
        checkTerms();
        checkRegistration();
        checkAnnouncements();
    }, [idKonta, checkTerms, checkRegistration, checkAnnouncements]);

    // SignalR: announcements
    useEffect(() => {
        registerAnnouncementCallback(() => {
            // [BANER] nowy komunikat moze byc paskiem — dajmy paskowi znac, ze ma sie odswiezyc
            window.dispatchEvent(new Event("announcement-received"));
            checkAnnouncements(true);
        });
    }, [registerAnnouncementCallback, checkAnnouncements]);

    // SignalR: terms update
    useEffect(() => {
        if (registerTermsCallback) {
            registerTermsCallback(() => {
                checkTerms(true);
            });
        }
    }, [registerTermsCallback, checkTerms]);

    /* 🔴 [zgłoszenie właściciela 2026-09-17] „na PC dalej mam ten sam komunikat".
       Komunikat zamknięty na laptopie i regulamin przyjęty na laptopie mają ZNIKNĄĆ tutaj, bez F5.

       ⚠️ `true` jest tu obowiązkowe: bez niego odczyt wróciłby z pamięci powłoki (TTL 10 min),
       callback zadziałałby, dziennik powiedziałby „ok", a ekran nie zmieniłby się wcale —
       najgorszy rodzaj cichej awarii, bo wygląda jak działająca naprawa. */
    useEffect(() => {
        if (!registerAnnouncementReadCallback) return;
        registerAnnouncementReadCallback(() => {
            // Pasek górny czyta osobną końcówkę, więc dostaje własny sygnał.
            window.dispatchEvent(new Event("announcement-read-elsewhere"));
            checkAnnouncements(true);
        });
    }, [registerAnnouncementReadCallback, checkAnnouncements]);

    useEffect(() => {
        if (!registerTermsAcceptedCallback) return;
        registerTermsAcceptedCallback(() => {
            setPendingTerms(null);          // bramka schodzi natychmiast, bez czekania na odpowiedź
            uniewaznij(KLUCZE_POWLOKI.AKCEPTACJE);
            checkTerms(true);
        });
    }, [registerTermsAcceptedCallback, checkTerms]);

    /* [A12-16] Serwer odrzucił zapis, bo konto nie przyjęło aktualnego regulaminu (403 terms_not_accepted).
       W normalnym obiegu okno regulaminu jest już na ekranie i do tego nie dojdzie; ten nasłuch ratuje
       sytuację, w której sprawdzenie regulaminu przy starcie się nie powiodło — zamiast surowego błędu
       człowiek dostaje okno z regulaminem i wie, co ma zrobić. */
    useEffect(() => {
        const onTermsRequired = () => { checkTerms(true); };
        window.addEventListener("terms-acceptance-required", onTermsRequired);
        return () => window.removeEventListener("terms-acceptance-required", onTermsRequired);
    }, [checkTerms]);

    // SignalR: in-app toast + nawigacja dla powiadomień (zadania + kanban)
    const TASK_TYPE_CODES = ["task_assigned", "task_shared", "task_completed", "task_unshared"];
    const KANBAN_TYPE_CODES = ["kanban_task_assigned", "kanban_task_due", "kanban_board_shared", "kanban_task_comment", "kanban_task_updated", "kanban_member_removed"];
    const REGISTRATION_TYPE_CODES = ["registration_approved", "registration_correction", "dept_assignment_changed"];

    useEffect(() => {
        registerToastCallback((notification) => {
            if (!notification) return;

            const code = notification.typeCode;

            /* [OBSZAR 20, 2026-09-18] BYŁO: klik „Otwórz" w dymku prowadził do celu, ale powiadomienie ZOSTAWAŁO
               nieprzeczytane — człowiek załatwił sprawę z dymka, a licznik przy dzwonku dalej je liczył
               (i liczył na każdym jego urządzeniu). Ten sam klik jest teraz odczytem, tak jak klik w pozycję
               dzwonka. Pozycje bez identyfikatora (syntetyczne) nie mają czego oznaczać. */
            const otworz = (cel) => {
                if (notification.id != null && typeof markAsRead === "function") markAsRead(notification.id);
                navigate(cel);
            };

            // Onboarding/przypisanie — odśwież bramkę (np. admin zresetował wniosek → formularz wraca).
            if (REGISTRATION_TYPE_CODES.includes(code)) {
                showToast(notification.message || notification.title, "info", 8);
                checkRegistration(true);
                return;
            }

            // [A15-21/A09-06] Powiadomienie ma INFORMOWAĆ, a nie porywać ekranu.
            //
            // Co było źle: przy każdym `ReceiveNotification` z kodem task_*/kanban_* Layout wołał
            // `navigate(...)` — bez żadnej interakcji użytkownika, w chwili przyjścia zdarzenia
            // z SignalR. Jedynym warunkiem było „nie jestem już na /zadania (albo /kanban)".
            // Skutek dla użytkownika: kadrowa wypełniająca drawer wniosku albo serwisant piszący
            // odpowiedź w zgłoszeniu byli w połowie zdania przerzucani na listę zadań — trasa się
            // odmontowywała i niezapisany formularz przepadał. Wystarczyło, że ktoś przypisał im
            // zadanie (POST /api/tasks jest dostępny dla każdego zalogowanego), a przy paru
            // przypisaniach z rzędu nie dało się pracować w innej sekcji.
            // Dlaczego tak naprawione: nawigacji nie usuwamy — przenosimy ją na przycisk „Otwórz"
            // w dymku (plus dotychczasowe kliknięcie wpisu w dzwonku, NotificationPanel). Skrót
            // jednym kliknięciem zostaje, ale to użytkownik decyduje, czy porzucić bieżącą stronę.

            // Toast dla zadań TODO
            if (TASK_TYPE_CODES.includes(code)) {
                const tasksPath = translatePath("/zadania");
                const alreadyThere = window.location.pathname.startsWith(tasksPath);
                showToast(notification.message || notification.title, "info", 8,
                    alreadyThere ? null : { actionLabel: "Otwórz", onAction: () => otworz(tasksPath) });
                return;
            }

            // Toast dla Kanban — link prowadzi do konkretnej tablicy (?board=)
            if (KANBAN_TYPE_CODES.includes(code)) {
                const kanbanPath = translatePath("/kanban");
                const alreadyThere = window.location.pathname.startsWith(kanbanPath);
                const targetPath = notification.link ? translatePath(notification.link) : null;
                showToast(notification.message || notification.title, "info", 8,
                    (targetPath && !alreadyThere)
                        ? { actionLabel: "Otwórz", onAction: () => otworz(targetPath) }
                        : null);
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [registerToastCallback, markAsRead]);

    // Gate 2. kroku: formularz konfiguracji konta pokazuje się gdy moduł aktywny, onboarding
    // nieukończony i NIE ma jeszcze wniosku w toku (lub został zwrócony do korekty/zresetowany).
    const needsProfileSetup = !!registrationStatus
        && registrationStatus.moduleActive
        && !registrationStatus.profileSetupCompleted
        && (!registrationStatus.requestStatus
            || registrationStatus.requestStatus === "correction"
            || registrationStatus.requestStatus === "reset");

    return (
        <div className={styles.layout}>
            {/* Skip-to-content — widoczny po naciśnięciu Tab */}
            <a href="#main-content" className="skip-to-content">
                Przejdź do treści
            </a>


            <Sidebar />

            {/* Telemetria aktywności (moduł „Analityka czasu pracy") — bez UI dla pracownika. */}
            <ActivityTracker />
            {/* [polecenie właściciela 2026-09-13] Maska ekranu na czas nagrania — działa wyłącznie u osób
                wskazanych w /admin/integrations; dla pozostałych to jedno małe pytanie przy starcie sesji. */}
            <MaskaNagrania />
            {/* [polecenie właściciela 2026-09-19] Ochrona treści — blokady skrótów i znak wodny włączane
                suwakami w /admin/integrations („Monitoring aktywności"). Stoi tu, bo ustawienie jest
                GLOBALNE: jedno zapytanie na wejście, a nałożenie dotyczy całej aplikacji, nie jednej strony. */}
            <OchronaTresci />

            {/* [E-5 2026-09-11] `data-obszar` to STABILNY uchwyt dla szuflady w trybie „na całą główną".
                Klasa jest modułowa (nazwa zahaszowana), a szuflada portaluje się do <body>, więc
                bez nazwanego atrybutu nie miałaby czego zmierzyć — a zmierzyć musi, bo lewa krawędź
                zależy od tego, czy pasek boczny jest zwinięty. */}
            <div
                data-obszar="tresc-glowna"
                className={`${styles.mainContent} ${isSidebarCollapsed ? styles.mainContentCollapsed : ""}`}
            >
                {/* [BC-10 2026-09-18] Tryb „zaloguj jako" — PIERWSZY element obszaru treści, nad banerem
                    i nagłówkiem. Świadomie tutaj, a nie nad całym układem: `.layout` jest wierszem flex
                    o wysokości 100vh (pasek boczny obok treści), więc pasek wstawiony tam stanąłby OBOK
                    menu, a nie nad nim. Poza sesją podglądu komponent zwraca null. */}
                <PasekPodgladu />
                {/* [polecenie właściciela 2026-10-02] TRYB TESTOWY POWIADOMIEŃ — baner dla każdego, kogo tryb dotyczy
                    ([2026-10-04] także dla osoby z dostępem do /admin/integrations: ona dostaje dodatkowo przycisk
                    „Wyłącz w Integracjach”). POZA bramką `komunikatyWidoczne`: to stan aplikacji, nie komunikat
                    z modułu „Komunikaty” — ukrycie tamtego modułu nie może go zgasić.
                    Poza trybem (i przy błędzie odczytu) komponent zwraca null. */}
                <BanerTrybuTestowego />
                {/* [BANER] Paski komunikatow z /admin/announcements — nad naglowkiem, nie scrolluja z trescia
                    (najwyzej dwa naraz, na telefonie jeden; reszta pod „+N”).
                    Za bramką widoczności modułu „Komunikaty" (patrz `komunikatyWidoczne` wyżej):
                    niewyrenderowany pasek nie pyta też serwera o `/announcements/topbar`. */}
                {komunikatyWidoczne && <AnnouncementTopBar />}
                <Header />
                <BanerWygasaniaHasla />
                <main className={styles.pageContent} id="main-content" tabIndex={-1}>
                    {/* Wrapper treści: flex:1 0 auto — ROŚNIE do treści i NIGDY się nie kurczy.
                        Bez tego flex-shrink zwijał stronę do ~1 ekranu (min-height:100%), treść
                        wylewała się poniżej, a stopka/sticky-toolbar lądowały w środku (bug scrolla). */}
                    <div className={styles.pageBody}>
                        {/* [A16-15] BYLO ZLE: jedyny ErrorBoundary stal w App.js wokol CALYCH <Routes>, wiec
                            wyjatek renderu w jednej podstronie zastepowal komunikatem bledu cala aplikacje.
                            SKUTEK DLA UZYTKOWNIKA: znikal panel boczny, naglowek, dzwonek powiadomien i czat -
                            nie dalo sie przejsc do innej sekcji, jedynym wyjsciem bylo 'Wroc do pulpitu' albo F5.
                            NAPRAWA: drugi ErrorBoundary tylko wokol tresci strony. Awaria podstrony zostaje w
                            obszarze tresci, nawigacja dziala dalej, a `resetKey` z adresem sprawia, ze przejscie
                            na inna strone samo czysci ekran bledu. Boundary z App.js zostaje jako siatka
                            bezpieczenstwa dla awarii poza trescia. */}
                        <ErrorBoundary resetKey={sciezkaStrony}>
                            <Outlet />
                        </ErrorBoundary>
                    </div>
                    {/* Stopka aplikacji — obecna na KAŻDEJ stronie; zawsze na samym dole treści. */}
                    <AppFooter />
                </main>
            </div>

            <MyNewTicketModal
                isOpen={isNewTicketModalOpen}
                onClose={closeNewTicketModal}
            />

            {/* Bramka onboardingu: regulamin → formularz konfiguracji konta → komunikaty (kolejność blokad) */}
            {/* [polecenie właściciela 2026-09-20] Regulamin i komunikat dostają ✕ w podglądzie tak samo
                jak rejestracja — zamknięcie lokalne na czas oglądania, nic nie idzie na serwer. */}
            {pendingTerms && !(podgladAktywny && zamknietePodczasPodgladu.has("regulamin")) ? (
                <TermsOfServiceModal
                    terms={pendingTerms}
                    onAccepted={handleTermsAccepted}
                    onZamknijWPodgladzie={podgladAktywny ? () => zamknijWPodgladzie("regulamin") : null}
                />
            ) : needsProfileSetup && !(podgladAktywny && zamknietePodczasPodgladu.has("profil")) ? (
                <ProfileSetupModal
                    status={registrationStatus}
                    onCompleted={handleRegistrationCompleted}
                    onZamknijWPodgladzie={podgladAktywny ? () => zamknijWPodgladzie("profil") : null}
                />
            ) : (
                /* `komunikatyWidoczne` STOI PRZED resztą warunku świadomie: gdy firma wyłączyła moduł,
                   bramka komunikatu nie ma prawa zatrzymać pracownika na wejściu do aplikacji.
                   Regulamin i formularz konfiguracji konta to osobne bramki i tej reguły NIE dzielą —
                   każda ma własny wyłącznik (regulamin: dokumenty prawne, formularz: `konfiguracjaKonta`). */
                komunikatyWidoczne
                && pendingAnnouncements.length > 0
                && !(podgladAktywny && zamknietePodczasPodgladu.has(`komunikat-${pendingAnnouncements[0]?.id}`)) && (
                    <AnnouncementModal
                        announcement={pendingAnnouncements[0]}
                        onRead={handleAnnouncementRead}
                        onZamknijWPodgladzie={podgladAktywny
                            ? () => zamknijWPodgladzie(`komunikat-${pendingAnnouncements[0]?.id}`)
                            : null}
                    />
                )
            )}

            {/* [polecenie właściciela 2026-10-03] Okno „Wygląd aplikacji” — raz na konto, dopiero gdy powłoka
                sprawdziła okna obowiązkowe i żadne nie czeka (regulamin → formularz konta → komunikat → wygląd).
                Pozostałe warunki (podgląd konta, trasy wyłączone, inne otwarte okno) w OknoWygladu.js. */}
            <OknoWygladuStartowe
                zalogowany={!!user}
                podgladAktywny={!!podgladAktywny}
                bramkiSprawdzone={bramkiSprawdzone.regulamin && bramkiSprawdzone.rejestracja && bramkiSprawdzone.komunikaty}
                bramkaAktywna={!!pendingTerms || needsProfileSetup || (komunikatyWidoczne && pendingAnnouncements.length > 0)}
                sciezka={sciezkaStrony}
            />

            <OchronaEkranu />

            <QuickAccessModal />
            {/* [S6] Wyszukiwarka globalna — okno nad aplikacją; na telefonie nieużywana (Header otwiera Szybki dostęp). */}
            <WyszukiwarkaGlobalna />

            <React.Suspense fallback={null}>
                <TeamsChatWidget />
            </React.Suspense>
        </div>
    );
};

export default Layout;
